import { useMutation } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { Button, Form, FormGroup, FormInput } from "semantic-ui-react";
import { gettingToKnowYou, setDefaults } from "../../api/moh";
import { useMoH } from "../../contexts/MoHContext";
import { Card, Page, PageHeader } from "../../components/ui";
import { style } from "../../style";

function GettingToKnowYou() {
  const {
    website,
    setWebsite,
    companyName,
    setCompanyName,
    industry,
    setIndustry,
    services,
    setServices,
    openTimes,
    setOpenTimes,
    tone,
    setTone,
  } = useMoH();

  const navigate = useNavigate();

  const gettingToKnowYouMutation = useMutation({
    mutationFn: () => {
      return gettingToKnowYou({ website: website });
    },
    onSuccess: (res) => {
      setCompanyName(res.data?.data?.company_name ?? "");
      setIndustry(res.data?.data?.industry ?? "");
      setServices(res.data?.data?.services ?? "");
      setOpenTimes(res.data?.data?.open_times ?? "");
      setTone(res.data?.data?.tone ?? "");
    },
    onError: (error) => {
      if (error?.response?.status !== 422) {
        toast.error("Failed to get information from website");
      }
    },
  });

  const defaultsMutation = useMutation({
    mutationFn: () => {
      return setDefaults({
        website,
        company_name: companyName,
        industry,
        services,
        open_times: openTimes,
        tone,
      });
    },
    onSuccess: (res) => {
      navigate(-1);
    },
    onError: (error) => {
      if (error?.response?.status !== 422) {
        toast.error("Failed to save you");
      }
    },
  });

  return (
    <Page maxWidth={900}>
      <PageHeader
        back={{ to: "/admin/moh", label: "Music on Hold" }}
        title="Getting to know you"
      />
      <Card style={{ padding: "20px 24px" }}>
        <Form onSubmit={() => gettingToKnowYouMutation.mutate()}>
          <p>
            Get started by entering your website URL below, we'll check this and
            pick out some key information to help generating your music on hold
          </p>

          <FormGroup widths="equal">
            <FormInput
              required
              fluid
              name="website"
              label="Website URL"
              placeholder="Enter your website URL"
              error={
                gettingToKnowYouMutation.error?.response?.data?.errors?.website
              }
              value={website}
              onChange={(e) => {
                setCompanyName("");
                setWebsite(e.target.value);
              }}
            />
          </FormGroup>

          <Button
            loading={gettingToKnowYouMutation.isLoading}
            type="submit"
            style={style.primaryButton}
          >
            {companyName ? "Try Again" : "Submit"}
          </Button>
        </Form>
      </Card>

      {companyName && (
        <Card style={{ padding: "20px 24px" }}>
          <Form onSubmit={() => defaultsMutation.mutate()}>
            <p>
              We've managed to identify the below information, you can edit it
              below before saving it anything needs changing.
            </p>

            <FormGroup widths="equal">
              <FormInput
                required
                fluid
                name="company_name"
                label="Company Name"
                error={
                  defaultsMutation.error?.response?.data?.errors?.company_name
                }
                value={companyName}
                onChange={(e) => setCompanyName(e.target.value)}
              />

              <FormInput
                required
                fluid
                name="industry"
                label="Industry"
                error={defaultsMutation.error?.response?.data?.errors?.industry}
                value={industry}
                onChange={(e) => setIndustry(e.target.value)}
              />
            </FormGroup>

            <FormGroup widths="equal">
              <FormInput
                required
                fluid
                name="services"
                label="Services"
                error={defaultsMutation.error?.response?.data?.errors?.services}
                value={services}
                onChange={(e) => setServices(e.target.value)}
              />
            </FormGroup>

            <FormGroup widths="equal">
              <FormInput
                required
                fluid
                name="open_times"
                label="Open Days & Hours"
                error={
                  defaultsMutation.error?.response?.data?.errors?.open_times
                }
                value={openTimes}
                onChange={(e) => setOpenTimes(e.target.value)}
              />

              <FormInput
                required
                fluid
                name="tone"
                label="Tone"
                error={defaultsMutation.error?.response?.data?.errors?.tone}
                value={tone}
                onChange={(e) => setTone(e.target.value)}
              />
            </FormGroup>

            <ActionButtonsContainer>
              <Button
                loading={defaultsMutation.isLoading}
                type="submit"
                style={style.primaryButton}
              >
                Save
              </Button>
            </ActionButtonsContainer>
          </Form>
        </Card>
      )}
    </Page>
  );
}

function ActionButtonsContainer({ children }) {
  return <div style={{ marginTop: "24px" }}>{children}</div>;
}

export default GettingToKnowYou;
